/**
 * Loftsails — Klaviyo browser client (public site ID only).
 * Uses Create Client Subscription (browser-only; server-side calls are ignored by Klaviyo).
 */
(function (global) {
  'use strict';

  var REVISION = '2026-04-15';
  var CONTENT_TYPE = 'application/vnd.api+json';
  var SUBSCRIPTIONS_URL = 'https://a.klaviyo.com/client/subscriptions/';
  var EVENTS_URL = 'https://a.klaviyo.com/client/events/';

  function parseApiError(response, json) {
    if (json && json.errors && json.errors.length) {
      var err = json.errors[0];
      return err.detail || err.title || 'Klaviyo request failed';
    }
    return 'Klaviyo request failed (HTTP ' + response.status + ')';
  }

  /**
   * RFC 4122 v4 identifier for one logical event.
   * Klaviyo deduplicates on (profile, metric, unique_id); without it, two
   * submissions in the same second collapse into one event.
   */
  function newUniqueId() {
    var cryptoObj = global.crypto;
    if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
      return cryptoObj.randomUUID();
    }
    var bytes = new Uint8Array(16);
    if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
      cryptoObj.getRandomValues(bytes);
    } else {
      for (var i = 0; i < bytes.length; i += 1) {
        bytes[i] = Math.floor(Math.random() * 256);
      }
    }
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    var hex = '';
    for (var j = 0; j < bytes.length; j += 1) {
      hex += (bytes[j] + 0x100).toString(16).slice(1);
    }
    return (
      hex.slice(0, 8) + '-' + hex.slice(8, 12) + '-' + hex.slice(12, 16) +
      '-' + hex.slice(16, 20) + '-' + hex.slice(20)
    );
  }

  /**
   * Subscribe one profile to a Klaviyo list (double opt-in handled by list settings).
   * @param {string} companyId - Public site ID (6 chars)
   * @param {string} listId - Klaviyo list ID
   * @param {object} profile - { email, firstName?, lastName?, properties? }
   * @param {object} [options] - { source?: string }
   * @returns {Promise<{status:number}>}
   */
  function subscribeToList(companyId, listId, profile, options) {
    options = options || {};
    if (!companyId || !listId) {
      return Promise.reject(new Error('Klaviyo company or list ID is missing'));
    }
    if (!profile || !profile.email) {
      return Promise.reject(new Error('Email is required'));
    }

    var attributes = { email: profile.email.trim() };
    if (profile.firstName) attributes.first_name = profile.firstName.trim();
    if (profile.lastName) attributes.last_name = profile.lastName.trim();
    if (profile.properties && typeof profile.properties === 'object') {
      attributes.properties = profile.properties;
    }

    var body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: options.source || 'Loftsails website',
          profile: {
            data: {
              type: 'profile',
              attributes: attributes
            }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: listId } }
        }
      }
    };

    return fetch(SUBSCRIPTIONS_URL + '?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: {
        'Content-Type': CONTENT_TYPE,
        Accept: CONTENT_TYPE,
        revision: REVISION
      },
      body: JSON.stringify(body)
    }).then(function (response) {
      if (response.status === 202) {
        return { status: 202 };
      }
      return response.json().catch(function () { return null; }).then(function (json) {
        throw new Error(parseApiError(response, json));
      });
    });
  }

  /**
   * Track one custom event for a profile (Create Client Event).
   * Used for per-submission triggers (flows), unlike list properties which are overwritten.
   * @param {string} companyId - Public site ID (6 chars)
   * @param {string} metricName - Klaviyo metric name, e.g. "Sail Pre-reservation 2027"
   * @param {object} profile - { email, firstName?, lastName? }
   * @param {object} [properties] - Event properties
   * @returns {Promise<{status:number}>}
   */
  function trackEvent(companyId, metricName, profile, properties) {
    if (!companyId || !metricName) {
      return Promise.reject(new Error('Klaviyo company or metric is missing'));
    }
    if (!profile || !profile.email) {
      return Promise.reject(new Error('Email is required'));
    }

    var attributes = { email: profile.email.trim() };
    if (profile.firstName) attributes.first_name = profile.firstName.trim();
    if (profile.lastName) attributes.last_name = profile.lastName.trim();

    var body = {
      data: {
        type: 'event',
        attributes: {
          properties: properties || {},
          metric: { data: { type: 'metric', attributes: { name: metricName } } },
          profile: { data: { type: 'profile', attributes: attributes } },
          time: new Date().toISOString(),
          unique_id: newUniqueId()
        }
      }
    };

    return fetch(EVENTS_URL + '?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: {
        'Content-Type': CONTENT_TYPE,
        Accept: CONTENT_TYPE,
        revision: REVISION
      },
      body: JSON.stringify(body)
    }).then(function (response) {
      if (response.status === 202) {
        return { status: 202 };
      }
      return response.json().catch(function () { return null; }).then(function (json) {
        throw new Error(parseApiError(response, json));
      });
    });
  }

  /** Push profile properties to Klaviyo onsite (klaviyo.js or legacy _learnq). */
  function identifyProfile(props) {
    if (!props || !props.$email) return;

    if (global.klaviyo && typeof global.klaviyo.identify === 'function') {
      global.klaviyo.identify(props);
      return;
    }

    global._learnq = global._learnq || [];
    global._learnq.push(['identify', props]);
  }

  global.LoftsailsKlaviyo = {
    REVISION: REVISION,
    subscribeToList: subscribeToList,
    trackEvent: trackEvent,
    identifyProfile: identifyProfile
  };
})(window);
